// =============================================
// NotesPage — "Your Knowledge Vault (YKV)" pillar shell: Masters · Notes · Reports
// =============================================
import React, { useCallback, useEffect, useState } from 'react';
import { confirmDiscardChanges, setFormDirty } from '../../lib/unsavedChanges';
import { NotesIcon } from '../../components/icons/ServiceIcons';
import { NotesBundle, loadNotesBundle } from './notesLib';
import { loadMediaMeta, MediaMeta } from './notesMedia';
import { aiStatus } from './kvAi';
import NotesTab from './NotesTab';
import KvCategoriesTab from './KvCategoriesTab';
import ReportsTab from './ReportsTab';
import './NotesPage.css';

type Tab = 'masters' | 'notes' | 'reports';
const EMPTY: NotesBundle = { notes: [], categories: [], links: [] };
const TABS: { id: Tab; label: string }[] = [
  { id: 'masters', label: 'Masters' },
  { id: 'notes', label: 'Notes' },
  { id: 'reports', label: 'Reports' },
];

const NotesPage: React.FC = () => {
  const [bundle, setBundle] = useState<NotesBundle>(EMPTY);
  const [media, setMedia] = useState<MediaMeta[]>([]);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('reports'); // landing = dashboard
  const [navSeq, setNavSeq] = useState(0);            // bump → remount active tab (re-land)
  const [openNoteId, setOpenNoteId] = useState<number | null>(null);
  const [openCategoryId, setOpenCategoryId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    const [b, m] = await Promise.all([loadNotesBundle(), loadMediaMeta().catch(() => [] as MediaMeta[])]);
    setBundle(b); setMedia(m); return b;
  }, []);

  useEffect(() => { (async () => { await reload(); aiStatus().then(setAiEnabled); setLoading(false); })(); }, [reload]);

  // Guard-then-land: confirm unsaved work, then switch + remount the target tab.
  const switchTab = useCallback(async (t: Tab) => {
    if (!(await confirmDiscardChanges())) return;
    setFormDirty(false);
    setOpenNoteId(null); setOpenCategoryId(null);
    setTab(t); setNavSeq(s => s + 1);
  }, []);

  // Deep links into the Notes tab.
  const goToNote = useCallback((id: number) => { setOpenCategoryId(null); setOpenNoteId(id); setTab('notes'); setNavSeq(s => s + 1); }, []);
  const goToCategory = useCallback((id: number) => { setOpenNoteId(null); setOpenCategoryId(id); setTab('notes'); setNavSeq(s => s + 1); }, []);

  if (loading) return <div className="notes-page"><div className="notes-empty">Loading your vault…</div></div>;

  return (
    <div className="notes-page kv-shell">
      <div className="kv-header">
        <div className="notes-title-row">
          <NotesIcon className="notes-title-icon" />
          <h2>Your Knowledge Vault <span className="kv-acronym">YKV</span></h2>
        </div>
        <p className="kv-tagline">Your lifelong knowledge &amp; skills — captured once, kept for good.</p>
      </div>

      <div className="kv-tabs" role="tablist">
        {TABS.map(t => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} className={`kv-tab ${tab === t.id ? 'active' : ''}`} onClick={() => void switchTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="kv-tabpanel">
        <React.Fragment key={`${tab}-${navSeq}`}>
          {tab === 'masters' && (
            <KvCategoriesTab categories={bundle.categories} links={bundle.links} onReload={reload} onOpenCategory={goToCategory} />
          )}
          {tab === 'notes' && (
            <NotesTab
              notes={bundle.notes}
              categories={bundle.categories}
              links={bundle.links}
              aiEnabled={aiEnabled}
              onReload={reload}
              openNoteId={openNoteId}
              openCategoryId={openCategoryId}
            />
          )}
          {tab === 'reports' && (
            <ReportsTab notes={bundle.notes} categories={bundle.categories} links={bundle.links} media={media} onReload={reload} onOpenNote={goToNote} onOpenCategory={goToCategory} />
          )}
        </React.Fragment>
      </div>
    </div>
  );
};

export default NotesPage;
